import { AUTH_TOKEN_KEY, isTokenInvalidError } from '../utils/authSession';

const ACTIVE_CONTEXT_KEY = 'activeContext';
const DEBUG_AUTH = process.env.REACT_APP_DEBUG_AUTH === 'true' || process.env.DEBUG_AUTH === 'true';

const normalizeBase = (value) => String(value || '').trim().replace(/\/$/, '');

// ✅ Host-based API routing (when env is not set)
const HOST_API_MAP = {
  'beta.crickarena.com': 'https://api.crickarena.com',
  'crickarena.com': 'https://api.codemates.co.in',
  'www.crickarena.com': 'https://api.codemates.co.in',
};

export const getApiBaseUrl = () => {
  const explicitBase = normalizeBase(
    process.env.REACT_APP_API_BASE_URL
      || process.env.REACT_APP_API_URL
      || process.env.VITE_API_BASE_URL
      || ''
  );

  // ✅ Highest priority: explicit env override
  if (explicitBase) return explicitBase;

  // ✅ SSR / Node (no window)
  if (typeof window === 'undefined') {
    if (process.env.NODE_ENV === 'development') return 'http://localhost:5001';
    return '';
  }

  const hostname = window.location?.hostname || '';

  // ✅ Local dev
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '0.0.0.0') {
    return 'http://localhost:5001';
  }

  // ✅ Host-based mapping for beta/prod
  const mapped = HOST_API_MAP[hostname];
  if (mapped) return mapped;

  // ✅ Safe fallback (prevents accidentally hitting frontend origin)
  return 'https://api.crickarena.com';
};

const API_BASE = getApiBaseUrl();

const getActiveContext = () => {
  if (typeof window === 'undefined') return null;
  try {
    return JSON.parse(window.localStorage.getItem(ACTIVE_CONTEXT_KEY) || 'null');
  } catch (_error) {
    return null;
  }
};

const getStoredToken = () => {
  if (typeof window === 'undefined') return '';
  return window.localStorage.getItem(AUTH_TOKEN_KEY) || window.sessionStorage.getItem(AUTH_TOKEN_KEY) || '';
};

const withContextHeaders = (headers = {}) => {
  const activeContext = getActiveContext() || {};
  const mergedHeaders = new Headers(headers);
  const token = getStoredToken();

  mergedHeaders.set('X-Active-Context-Type', activeContext.type || 'individual');
  mergedHeaders.set('X-Active-Role-Type', activeContext.type || 'individual');
  mergedHeaders.set('X-Active-Entity-Type', activeContext.entityType || '');
  mergedHeaders.set('X-Active-Entity-Id', activeContext.entityId || '');

  if (activeContext.entityType && activeContext.entityId) {
    mergedHeaders.set('X-Entity-Type', activeContext.entityType);
    mergedHeaders.set('X-Entity-Id', activeContext.entityId);
  }

  if (token && !mergedHeaders.has('Authorization')) {
    mergedHeaders.set('Authorization', `Bearer ${token}`);
  }

  return mergedHeaders;
};

const isSessionExpiredResponse = async (response) => {
  if (!response || response.status !== 401) {
    return false;
  }

  const cloned = response.clone();
  const payload = await cloned.json().catch(() => ({}));
  return isTokenInvalidError(payload, response.statusText);
};

export const initApiClient = () => {
  if (typeof window === 'undefined' || window.__CRICKARENA_FETCH_PATCHED__) return;

  const originalFetch = window.fetch.bind(window);
  if (!window.__CRICKARENA_ORIGINAL_FETCH__) {
    window.__CRICKARENA_ORIGINAL_FETCH__ = originalFetch;
  }

  window.fetch = async (input, init = {}) => {
    const request = input instanceof Request ? input : null;
    const method = (init?.method || request?.method || 'GET').toUpperCase();
    const requestUrl = typeof input === 'string' ? input : request?.url || '';
    const requestHeaders = withContextHeaders(init.headers || request?.headers || {});
    if (DEBUG_AUTH) {
      // eslint-disable-next-line no-console
      console.debug(`[API] request ${method} ${requestUrl} hasAuth=${requestHeaders.has('Authorization')}`);
    }

    try {
      const nextInit = { ...init, headers: requestHeaders };
      const response = await originalFetch(input, nextInit);

      if (DEBUG_AUTH) {
        // eslint-disable-next-line no-console
        console.debug(`[API] response ${response.status} ${response.url || requestUrl}`);
      }

      if (await isSessionExpiredResponse(response)) {
        window.dispatchEvent(new CustomEvent('auth-session-expired'));
      }

      return response;
    } catch (error) {
      if (DEBUG_AUTH) {
        // eslint-disable-next-line no-console
        console.debug(`[API] network error ${method} ${requestUrl}`, error);
      }
      return Promise.reject(error);
    }
  };

  window.__CRICKARENA_FETCH_PATCHED__ = true;
};

export const contextAwareHeaders = (headers = {}) => withContextHeaders(headers);

export const buildUrl = (path) => {
  const requestedPath = String(path || '');

  // already absolute
  if (/^https?:\/\//i.test(requestedPath)) {
    return requestedPath;
  }

  const normalizedPath = requestedPath.startsWith('/') ? requestedPath : `/${requestedPath}`;
  const base = normalizeBase(API_BASE);

  // If base is empty, fallback to relative URLs
  if (!base) {
    return normalizedPath;
  }

  if (!/^https?:\/\//i.test(base)) {
    return normalizedPath;
  }

  return new URL(normalizedPath, base).toString();
};
